<template>
  <div
    :class="[
      'rb-media-content rb-media-video rb-media-on-hand',
      { 'rb-active': active, 'rb-started': started },
    ]"
    ref="elOnHandVideosGallery"
  >
    <div class="rb-media-content-wrap rb-video-wrap rb-keen-wrap">
      <template v-if="displayVideos.length > 1">
        <button
          class="rb-slider-arrow rb-slider-arrow-left"
          id="btn-video-images-prev"
          @click.prevent="prevSlide"
        >
          <icon-arrow-left />
        </button>
        <button
          class="rb-slider-arrow rb-slider-arrow-right"
          id="btn-video-images-next"
          @click.prevent="nextSlide"
        >
          <icon-arrow-right />
        </button>
      </template>
      <div
        class="keen-slider"
        ref="elOnHandGallery"
        style="width: 100%; height: 100%"
      >
        <div
          :class="['keen-slider__slide rb-slider-slide']"
          v-for="(video, indexVideo) in displayVideos"
          :key="`gallery-image-${indexVideo}`"
        >
          <button
            aria-label="Play Video"
            class="rb-btn-play"
            v-if="!isPlaying"
            @click.prevent="play"
            id="btn-video-play"
          >
            <icon-play />
          </button>
          <button
            aria-label="Stop Video"
            class="rb-btn-play rb-btn-pause"
            v-if="isPlaying"
            @click.prevent="pause"
            id="btn-video-pause"
          >
            <icon-pause />
          </button>
          <video
            @pause="onPause"
            class="video"
            muted="muted"
            playsinline
            loop
            :ref="`elOnHandVideo-${video.indexVideo}`"
            :id="`elOnHandVideo-${video.indexVideo}`"
            :src="video.url"
          >
            <source
              :src="videoSource.url"
              :type="videoSource.mime_type"
              v-for="(videoSource, indexVideoSource) in video.sources"
              :key="`video-source-${indexVideoSource}`"
            />
          </video>
        </div>
      </div>
      <div class="rb-keen-dots">
        <button
          :class="['rb-keen-dot', { 'rb-active': currentSlide === indexVideo }]"
          v-for="(video, indexVideo) in displayVideos"
          :key="`gallery-dot-${indexVideo}`"
          @click.prevent="gotoSlide(indexVideo)"
        ></button>
      </div>
    </div>
  </div>
</template>
<script>
/**
 * Displays a carousel of product videos filtered by variant
 *
 * @props
 * active: Boolean
 * videos: Array
 *
 * @implements
 * CommMixin
 * TrackMixin
 *
 * @listens
 * rb-selected-options-updated
 * rb-step-display-update
 *
 * @emits
 * rb-disable-on-hand-video
 * rb-enable-on-hand-video
 */
import KeenSlider from "keen-slider";
import IconArrowLeft from "@/components/icons/IconArrowLeft";
import IconArrowRight from "@/components/icons/IconArrowRight";
import IconPlay from "@/components/icons/IconPlay";
import IconPause from "@/components/icons/IconPause";
import CommMixin from "@/mixins/CommMixin";
import TrackMixin from "@/mixins/TrackMixin";
export default {
  components: { IconPlay, IconPause, IconArrowRight, IconArrowLeft },
  mixins: [CommMixin, TrackMixin],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    videos: {
      type: Array,
      default: () => ({}),
    },
  },
  data: function () {
    return {
      isPlaying: false,
      slider: null,
      currentSlide: 0,
      started: false,
      selectedOptions: [],
      mappings: null,
    };
  },
  beforeMount: function () {
    this.subscribe(
      "rb-selected-options-updated",
      this.onSelectedOptionsUpdated
    );
    this.subscribe("rb-step-display-update", this.onStepDisplayUpdate);
  },
  watch: {
    displayVideos: function () {
      if (this.displayVideos.length === 0) {
        this.broadcast("rb-disable-on-hand-video");
      } else {
        this.broadcast("rb-enable-on-hand-video");
      }
    },
  },
  computed: {
    displayVideos: function () {
      // reordering the array so it displays first the ones that are actually tagged and at the end the ones that are not
      var displayVideos = this.videos.filter((video) =>
        this.styleDisplayMedia(video)
      );
      return displayVideos;
    },
  },
  methods: {
    play: function (event) {
      if (this.displayVideos.length == 0) return;
      var refVideo =
        this.$refs[`elOnHandVideo-${this.displayVideos[this.currentSlide].id}`];
      if (refVideo && refVideo.length > 0) {
        refVideo[0].play();
      }
      this.isPlaying = true;
      this.trackEvent(
        this.formatTracking(this.getTrackId(event), this.TRACK_CLICK),
        this.getTrackId(event)
      );
    },
    pause: function (event) {
      this.onPause();
      this.trackEvent(
        this.formatTracking(this.getTrackId(event), this.TRACK_CLICK),
        this.getTrackId(event)
      );
    },
    onPause: function () {
      if (this.displayVideos.length == 0) return;
      var refVideo =
        this.$refs[`elOnHandVideo-${this.displayVideos[this.currentSlide].id}`];
      if (refVideo && refVideo.length > 0) {
        refVideo[0].pause();
      }
      this.isPlaying = false;
    },
    onStepDisplayUpdate: function (step) {
      if (step.id === "summary") {
        this.$nextTick(() => {
          this.refresh();
        });
      }
    },
    styleDisplayMedia: function (media) {
      if (this.selectedOptions.length === 0) return true;
      var show = true;
      this?.selectedOptions?.forEach((selectedOption) => {
        let option = selectedOption?.choice?.label?.toLowerCase();
        if(option == 'round brilliant') {
          option = 'round';
        }
        if (!media?.options?.includes(option)) {
          show = false;
        }
      });

      return show;
    },
    onSelectedOptionsUpdated: function (data) {
      this.selectedOptions = data.selectedOptions;
      this.mappings = data.mappings;
      this.$nextTick(() => {
        this.refresh();
      });
    },
    start: function () {
      if (this.started) return;
      setTimeout(() => {
        this.initSlider();
        this.started = true;
      }, 250);
    },
    initSlider: function () {
      this.slider = new KeenSlider(this.$refs.elOnHandGallery, {
        loop: false,
        slides: { perView: 1 },
        created: () => {
          this.play();
        },
        slideChanged: this.slideChanged,
      });
    },
    prevSlide: function (event) {
      this.slider.prev();
      this.trackEvent(
        this.formatTracking(this.getTrackId(event), this.TRACK_CLICK),
        this.getTrackId(event)
      );
    },
    nextSlide: function (event) {
      this.slider.next();
      this.trackEvent(
        this.formatTracking(this.getTrackId(event), this.TRACK_CLICK),
        this.getTrackId(event)
      );
    },
    gotoSlide: function (idx) {
      this.slider.moveToIdx(idx);
    },
    slideChanged: function (slider) {
      this.currentSlide = slider.track.details.abs;
    },
    refresh: function () {
      this.started = false;
      this.currentSlide = 0;
      if (this.slider) {
        this.slider.destroy();
      }
      setTimeout(() => {
        this.initSlider();
        this.started = true;
      }, 250);
    },
  },
};
</script>
